import { useAtom, useAtomValue } from 'jotai'
import { ReactNode, Suspense, useCallback, useMemo } from 'react'

import { PAGE_TITLES } from '@cowprotocol/common-const'
import { useMediaQuery } from '@cowprotocol/common-hooks'
import { Dialog, DialogOrInline, Media, Modal } from '@cowprotocol/ui'

import { useLingui } from '@lingui/react/macro'
import { useInjectedWidgetParams } from 'entities/injectedWidget'
import { TabOrderTypes } from 'entities/routes/routes.atom'
import { useParams } from 'react-router'

import { Loading } from 'legacy/components/FlashingLoading'

import {
  advancedOrdersAtom,
  advancedOrdersDerivedStateAtom,
  AdvancedOrdersWidget,
  SetupAdvancedOrderAmountsFromUrlUpdater,
  useAdvancedOrdersDerivedStateToFill,
} from 'modules/advancedOrders'
import { PageTitle } from 'modules/application'
import { limitOrdersSettingsAtom } from 'modules/limitOrders'
import { OrdersTableWidget, ordersTableStateAtom, useOrdersTable } from 'modules/ordersTable'
import { PriceChart } from 'modules/priceChart'
import * as styledEl from 'modules/trade'
import {
  TradeRouteRedirect,
  getLimitPriceFromReceiveAmount,
  useOrdersTableDrawerState,
  useSetOrdersTableDrawerOpen,
} from 'modules/trade'
import {
  SetupFallbackHandlerWarning,
  TwapConfirmModal,
  TwapFormWidget,
  TwapSellAmountDescription,
  TwapUpdaters,
  useIsFallbackHandlerRequired,
  useMapTwapCurrencyInfo,
  useScaledReceiveAmountInfo,
  useTwapFormState,
  useTwapSlippage,
  TwapFormState,
  twapRateInvertedAtom,
} from 'modules/twap'

import { Routes } from 'common/constants/routes'
import { HydrateAtom } from 'common/state/HydrateAtom'
import { isTwapSupportedChain } from 'common/utils/isTwapSupportedChain'

const ADVANCED_ORDERS_MAX_WIDTH = '1800px'

// eslint-disable-next-line max-lines-per-function
export function AdvancedOrdersPage(): ReactNode {
  useOrdersTable(TabOrderTypes.ADVANCED)

  const params = useParams()
  const { i18n, t } = useLingui()
  const { isUnlocked } = useAtomValue(advancedOrdersAtom)
  const [isInverted, setIsInverted] = useAtom(twapRateInvertedAtom)
  const { ordersTableOnLeft } = useAtomValue(limitOrdersSettingsAtom)

  const { pendingOrders } = useAtomValue(ordersTableStateAtom)
  const isFallbackHandlerRequired = useIsFallbackHandlerRequired()

  const twapFormValidation = useTwapFormState()
  const twapSlippage = useTwapSlippage()
  const mapTwapCurrencyInfo = useMapTwapCurrencyInfo()
  const { hideOrdersTable } = useInjectedWidgetParams()
  const { isOpen: isOrdersTableDrawerOpen } = useOrdersTableDrawerState()
  const setOrdersTableDrawerOpen = useSetOrdersTableDrawerOpen()
  const isUpToLarge = useMediaQuery(Media.upToLarge(false))

  const handleOrdersTableDrawerOpenChange = useCallback(
    (open: boolean) => {
      setOrdersTableDrawerOpen(open)
    },
    [setOrdersTableDrawerOpen],
  )

  const disablePriceImpact =
    twapFormValidation === TwapFormState.SELL_AMOUNT_TOO_SMALL ||
    twapFormValidation === TwapFormState.RECEIVE_ZERO_FROM_NETWORK_COSTS
  const advancedWidgetParams = { disablePriceImpact }
  const advancedOrdersDerivedStateToFill = useAdvancedOrdersDerivedStateToFill(twapSlippage)
  const { inputCurrency, outputCurrency } = advancedOrdersDerivedStateToFill
  const receiveAmountInfo = useScaledReceiveAmountInfo()
  const worstAcceptablePrice = useMemo(
    () => (receiveAmountInfo ? getLimitPriceFromReceiveAmount(receiveAmountInfo) : null),
    [receiveAmountInfo],
  )
  const referenceLines = useMemo(
    () => [
      {
        id: 'trade:worst-acceptable-price',
        label: t`Worst acceptable price`,
        price: worstAcceptablePrice,
        variant: 'worst-acceptable-price' as const,
      },
    ],
    [worstAcceptablePrice, t],
  )

  if (!params.chainId) {
    return <TradeRouteRedirect route={Routes.ADVANCED_ORDERS} />
  }

  if (!isTwapSupportedChain(Number(params.chainId))) {
    return <TradeRouteRedirect route={Routes.SWAP} />
  }

  return (
    <HydrateAtom atom={advancedOrdersDerivedStateAtom} state={advancedOrdersDerivedStateToFill}>
      <PageTitle title={i18n._(PAGE_TITLES.ADVANCED)} />
      <SetupAdvancedOrderAmountsFromUrlUpdater />
      <styledEl.PageWrapper
        isUnlocked={isUnlocked}
        maxWidth={ADVANCED_ORDERS_MAX_WIDTH}
        secondaryOnLeft={ordersTableOnLeft}
        hideOrdersTable={hideOrdersTable || isUpToLarge || !isUnlocked}
      >
        <styledEl.PrimaryWrapper>
          {isFallbackHandlerRequired && pendingOrders.length > 0 && <SetupFallbackHandlerWarning />}
          <AdvancedOrdersWidget
            updaters={<TwapUpdaters />}
            confirmContent={<TwapConfirmModal />}
            params={advancedWidgetParams}
            mapCurrencyInfo={mapTwapCurrencyInfo}
            topContent={<TwapSellAmountDescription />}
          >
            {(tradeWarnings, captcha) => (
              <>
                {/*TODO: conditionally display a widget for current advanced order type*/}
                <TwapFormWidget tradeWarnings={tradeWarnings} captcha={captcha} />
              </>
            )}
          </AdvancedOrdersWidget>
        </styledEl.PrimaryWrapper>

        {isUnlocked && (
          <styledEl.SecondaryColumn>
            <PriceChart
              inputCurrency={inputCurrency}
              outputCurrency={outputCurrency}
              selectedCurrency={isInverted ? outputCurrency : inputCurrency}
              onSelectCurrency={(currency) => setIsInverted(Boolean(outputCurrency?.equals(currency)))}
              referenceLines={referenceLines}
            />
            {!hideOrdersTable && (
              <DialogOrInline
                isDialog={isUpToLarge}
                isOpen={isOrdersTableDrawerOpen}
                onOpenChange={handleOrdersTableDrawerOpenChange}
              >
                <Modal.Root className="trade-orders-table">
                  {isUpToLarge && (
                    <Modal.Header
                      sticky
                      title={t`TWAP orders`}
                      titleAs={Dialog.Title}
                      onClose={() => setOrdersTableDrawerOpen(false)}
                    />
                  )}
                  <styledEl.SecondaryWrapper $inDrawer={isUpToLarge}>
                    <Suspense fallback={<Loading />}>
                      <OrdersTableWidget orderType={TabOrderTypes.ADVANCED} />
                    </Suspense>
                  </styledEl.SecondaryWrapper>
                </Modal.Root>
              </DialogOrInline>
            )}
          </styledEl.SecondaryColumn>
        )}
      </styledEl.PageWrapper>
    </HydrateAtom>
  )
}
